'use client';

import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { fillTemplate } from '@eliadmin/core/src/utils/nt/text';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { useShippingDates } from './shipping';
import type { stockState } from './use-active-variant';

export type StockTexts = {
  /** „Skladom {pocet} ks“. */
  inStock: string;
  /** „14:00“. */
  cutoff: string;
  /** „Odosielame dnes, ak objednáte do {cas}“. */
  shipToday: string;
  /** „Odosielame {den}“. */
  shipLater: string;
  /** „v pondelok | … | v piatok“. */
  weekdays: string;
};

/** Separator dot of the one-line row (from sm); below it the shipping line stands alone. */
const DOT = 'hidden size-1 shrink-0 rounded-full bg-[#CDD2CA] sm:block';

/**
 * Figma „Stock row“ (112:1289): status dot, „Skladom {pocet} ks“ in text/brand, then – after mount,
 * in the store time zone – „Odosielame dnes, ak objednáte do 14:00“ or „Odosielame v pondelok“. An
 * item without stock shows the availability / unavailability name of the administration instead
 * (red when it cannot be ordered).
 */
export function HeroStock({
  stock,
  texts,
}: {
  stock: ReturnType<typeof stockState>;
  texts: StockTexts;
}) {
  const locale = useLocaleStore(state => state.currentLocale);
  const dates = useShippingDates(texts.cutoff, texts.weekdays, locale);
  const label = stock.inStock ? fillTemplate(texts.inStock, { pocet: stock.count }) : stock.label;
  if (!label) return null;

  const shipping =
    stock.inStock && dates
      ? dates.shipsToday
        ? fillTemplate(texts.shipToday, { cas: texts.cutoff.trim() })
        : fillTemplate(texts.shipLater, { den: dates.shipDay })
      : '';

  return (
    <p className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2">
      <span className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className={cn(
            'size-2 shrink-0 rounded-full',
            stock.sellable ? 'bg-[#127A26]' : 'bg-[#D92D20]',
          )}
        />
        <span
          className={cn('nt-body-m-semibold', stock.sellable ? 'text-[#0E6A20]' : 'text-[#B42318]')}
        >
          {label}
        </span>
      </span>
      {shipping ? (
        <span className="flex items-center gap-2 pl-4 sm:pl-0">
          <span aria-hidden="true" className={DOT} />
          <span className="nt-body-m text-[#363D36]">{shipping}</span>
        </span>
      ) : null}
    </p>
  );
}
